import { Trans } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from 'designSystem/components/Item'
import { Spinner } from 'designSystem/components/Spinner'
import { Text } from 'designSystem/components/Text'
import { CircleCheck } from 'lucide-react'
import React, { type ReactNode } from 'react'
import styled from 'styled-components'

import { PhotoRemoveButton } from './PhotoRemoveButton'
import type { PhotoUploadPhase } from './usePhotoUploadQueue'

const UploadedIcon = styled(CircleCheck)`
  color: var(--success);
`

const isFailed = (phase: PhotoUploadPhase): boolean =>
  phase === 'upload_failed' || phase === 'attach_failed'

function StatusTitle({ phase }: { phase: PhotoUploadPhase }) {
  switch (phase) {
    case 'uploading':
      return <Trans>Uploading…</Trans>
    case 'attaching':
      return <Trans>Attaching…</Trans>
    case 'attached':
      return <Trans>Uploaded</Trans>
    case 'removing':
      return <Trans>Removing…</Trans>
    case 'upload_failed':
      return <Trans>Upload failed</Trans>
    case 'attach_failed':
      return <Trans>Attach failed</Trans>
  }
}

export const PhotoUploadRow: React.FC<{
  previewUrl: string
  fileName: string
  phase: PhotoUploadPhase
  error: string | null
  retryable: boolean
  onRetry: () => void
  onDiscard: () => void
  onRemove: () => void
  attachedAction?: ReactNode
}> = ({
  previewUrl,
  fileName,
  phase,
  error,
  retryable,
  onRetry,
  onDiscard,
  onRemove,
  attachedAction,
}) => {
  const busy =
    phase === 'uploading' || phase === 'attaching' || phase === 'removing'

  return (
    <Item variant='outline' size='xs'>
      <ItemMedia variant='image' align='center' size='lg'>
        <img src={previewUrl} alt={fileName} />
      </ItemMedia>
      {(busy || phase === 'attached') && (
        <ItemMedia variant='icon' align='center'>
          {busy ? <Spinner /> : <UploadedIcon />}
        </ItemMedia>
      )}
      <ItemContent>
        <ItemDescription>
          <StatusTitle phase={phase} />
        </ItemDescription>
        <ItemTitle title={fileName}>{fileName}</ItemTitle>
        {error && (
          <Text color='destructive' variant='caption'>
            {error}
          </Text>
        )}
      </ItemContent>
      <ItemActions variant='trailing'>
        {isFailed(phase) && retryable && (
          <Button type='button' variant='outline' onClick={onRetry}>
            <Trans>Retry</Trans>
          </Button>
        )}
        {isFailed(phase) && (
          <Button type='button' variant='outline' onClick={onDiscard}>
            <Trans>Discard</Trans>
          </Button>
        )}
        {phase === 'attached' && attachedAction}
        {phase === 'attached' && <PhotoRemoveButton onRemove={onRemove} />}
      </ItemActions>
    </Item>
  )
}
